import { useApp } from 'contexts/app';
import { useAppBar } from 'contexts/appBar';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useNavigationGuard } from 'contexts/navigationGuard';
import { useSnackbar } from 'contexts/snackbar';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import rubricService from 'services/rubric';
import { Button, Stack } from 'shell/components';
import { RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT } from 'utils/constants';
import { getWordCount, isValidRate, roundCriteriaOptionRates, sortCriteria } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const PageContext = createContext();

const getDefaultForm = (t) => ({
    title: '',
    criteria: [
        {
            id: uuid(),
            title: t('rubric.form.title-default', 'Criterion Name'),
            description: t('rubric.form.description-default', 'Description'),
            options: [
                {
                    id: uuid(),
                    title: '',
                    description: '',
                    rate: '',
                },
                {
                    id: uuid(),
                    title: '',
                    description: '',
                    rate: '',
                },
            ],
        },
    ],
    sort: 'desc',
});

const defaultErrors = {
    title: '',
    criteria: {},
};

const PageProvider = (props) => {
    const { setRubrics } = useApp();
    const { setNode } = useAppBar();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { onBeforeNavigate } = useNavigationGuard();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [form, setForm] = useState(getDefaultForm(t));
    const [errors, setErrors] = useState(defaultErrors);
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

    const validateTitle = useCallback(() => {
        const error = form.title.trim() ? '' : t('error.empty-name', 'Please enter a name');
        setErrors((current) => {
            return { ...current, title: error };
        });
        return !Boolean(error);
    }, [form.title, t]);

    const validateCriteria = useCallback(() => {
        let error = false;
        form.criteria.forEach((criterion) => {
            criterion.options.forEach((option) => {
                const isRateInvalid = !isValidRate(option.rate);
                const isDescriptionInvalid =
                    getWordCount(option.description) > RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT;
                const hasOptionError = isRateInvalid || isDescriptionInvalid;
                if (hasOptionError) error = true;
                setErrors((current) => ({
                    ...current,
                    criteria: {
                        ...(current?.criteria || {}),
                        [criterion.id]: {
                            ...(current?.criteria?.[criterion.id] || {}),
                            [option.id]: {
                                rate: isRateInvalid ? t('error.empty-number', 'Please enter a number') : '',
                                description: isDescriptionInvalid
                                    ? t('error.word-count-exceeded', 'Word count exceeded')
                                    : '',
                            },
                        },
                    },
                }));
            });
        });
        return !Boolean(error);
    }, [form.criteria, t]);

    const validateAll = useCallback(() => {
        const isTitleValid = validateTitle();
        const areCriteriaValid = validateCriteria();
        if (isTitleValid && areCriteriaValid) return true;
        return false;
    }, [validateTitle, validateCriteria]);

    const onClickCancel = useCallback(() => {
        setDialog({
            open: true,
            message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
            description: t(
                'common:dialog.unsaved-changes.description',
                'If you exit without saving, all unsaved changes will be lost.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.exit', 'Exit'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                history.push('/rubrics/get-started');
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    }, [closeDialog, history, setDialog, t]);

    const onClickCreate = useCallback(() => {
        setHasClickedSubmit(true);
        const isValid = validateAll();
        if (isValid) {
            const timestamp = new Date().getTime();
            const criteriaRoundedOptionRates = roundCriteriaOptionRates(form.criteria);
            const sortedCriteria = sortCriteria(criteriaRoundedOptionRates, form.sort);
            const rubric = {
                id: uuid(),
                active: true,
                created_on: timestamp,
                modified_on: timestamp,
                rubric: { ...form, criteria: sortedCriteria },
                user_id: auth.user_id,
            };
            setRubrics((current) => [...current, rubric]);
            rubricService.createRubric(rubric);
            history.push('/rubrics');
        } else {
            message.error(t('error.invalid-fields', 'One or more fields are not valid.'));
        }
    }, [auth.user_id, form, history, message, setRubrics, validateAll, t]);

    useEffect(() => {
        setNode(
            <Stack alignItems={'center'} direction={'row'} justifyContent={'flex-end'} spacing={4}>
                <Button color={'neutral'} variant={'tertiary'} onClick={onClickCancel}>
                    {t('common:controls.cancel', 'Cancel')}
                </Button>
                <Button onClick={onClickCreate}>{t('rubric.create-rubric', 'Create Rubric')}</Button>
            </Stack>
        );

        return () => setNode(null);
    }, [onClickCancel, onClickCreate, setNode, t]);

    useEffect(() => {
        onBeforeNavigate((navigate) => {
            setDialog({
                open: true,
                message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
                description: t(
                    'common:dialog.unsaved-changes.description',
                    'If you exit without saving, all unsaved changes will be lost.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.exit', 'Exit'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    navigate();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        });

        return () => {
            onBeforeNavigate(null);
        };
    }, [closeDialog, onBeforeNavigate, setDialog, t]);

    return (
        <PageContext.Provider
            value={{
                form,
                setForm,
                errors,
                setErrors,
                hasClickedSubmit,
                validateTitle,
                validateCriteria,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
